import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CoinPackageSummary, ComplianceStatusResponse, UserSpendLimits } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatCoins, formatUsdCents } from '@fetmode/utils';
import { Button } from '@fetmode/ui';

import '../shows/CpmRunwayOverlay.css';

export interface CoinShopOverlayProps {
  isOpen: boolean;
  coinBalance: number;
  neededCoins: number | null;
  onClose: () => void;
  /** Return `false` to keep the shop open (e.g. purchase did not cover needed coins). */
  onPurchased: (walletTotal: number) => boolean | void;
}

export function CoinShopOverlay({
  isOpen,
  coinBalance,
  neededCoins,
  onClose,
  onPurchased,
}: CoinShopOverlayProps): ReactElement | null {
  const { api } = useAuth();
  const [packages, setPackages] = useState<CoinPackageSummary[]>([]);
  const [limits, setLimits] = useState<UserSpendLimits | null>(null);
  const [compliance, setCompliance] = useState<ComplianceStatusResponse | null>(null);
  const [isBuying, setIsBuying] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setBuyError(null);
    void Promise.all([api.listCoinPackages(), api.getSpendLimits(), api.getComplianceStatus()])
      .then(([pkgResult, spendLimits, status]) => {
        if (cancelled) {
          return;
        }
        setPackages(pkgResult.packages.filter((pkg) => pkg.enabled));
        setLimits(spendLimits.limits);
        setCompliance(status);
      })
      .catch(() => {
        if (!cancelled) {
          setBuyError(t('common.show.cpmBuyFailed'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, isOpen]);

  const buyCoins = useCallback(
    async (input: { packageId?: string; coins?: number }): Promise<void> => {
      setIsBuying(true);
      setBuyError(null);
      try {
        const status = await api.getComplianceStatus();
        setCompliance(status);
        if (!status.canAccess) {
          setBuyError(t('common.show.cpmBuyVerify'));
          return;
        }
        const created = await api.createCoinPurchase(input);
        const completed = await api.confirmCoinPurchase(created.purchaseId);
        const shouldClose = onPurchased(completed.wallet.total) !== false;
        if (shouldClose) {
          onClose();
        }
      } catch (err: unknown) {
        setBuyError(err instanceof ApiError ? err.message : t('common.show.cpmBuyFailed'));
      } finally {
        setIsBuying(false);
      }
    },
    [api, onClose, onPurchased],
  );

  const handleAutoReload = useCallback(async (): Promise<void> => {
    const amount = limits?.autoTopUpAmountCoins ?? 500;
    try {
      await api.updateSpendLimits({
        autoTopUpEnabled: true,
        autoTopUpAmountCoins: amount,
        autoTopUpTriggerCoins: limits?.autoTopUpTriggerCoins ?? 50,
      });
    } catch {
      // Purchase can still proceed; the toggle is best-effort.
    }
    await buyCoins({ coins: amount });
  }, [api, buyCoins, limits]);

  if (!isOpen) {
    return null;
  }

  const body =
    neededCoins !== null && neededCoins > coinBalance
      ? t('common.show.cpmShopNeed')
          .replace('{{needed}}', formatCoins(neededCoins))
          .replace('{{balance}}', formatCoins(coinBalance))
      : t('common.show.cpmShopBalance').replace('{{balance}}', formatCoins(coinBalance));

  return createPortal(
    <div className="cpm-runway cpm-runway--global" role="dialog" aria-modal="true">
      <div className="cpm-runway__card">
        <button
          type="button"
          className="cpm-runway__dismiss"
          aria-label={t('common.show.cpmDismiss')}
          onClick={onClose}
        >
          ×
        </button>
        <h2 className="cpm-runway__title">{t('common.show.cpmShopTitle')}</h2>
        <p className="cpm-runway__body">{body}</p>
        {buyError ? <p className="cpm-runway__error">{buyError}</p> : null}
        {packages.length > 0 ? (
          <div className="cpm-runway__packages">
            {packages.slice(0, 6).map((pkg) => (
              <button
                key={pkg.id}
                type="button"
                className="cpm-runway__package"
                disabled={isBuying}
                onClick={() => {
                  void buyCoins({ packageId: pkg.id });
                }}
              >
                <strong>{pkg.label}</strong>
                <span>{formatCoins(pkg.totalCoins)}</span>
                <span className="cpm-runway__package-price">{formatUsdCents(pkg.usdCents)}</span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="cpm-runway__actions">
          <Button
            label={isBuying ? t('common.show.cpmBuying') : t('common.show.cpmAutoReload')}
            disabled={isBuying}
            onClick={() => {
              void handleAutoReload();
            }}
          />
          <p className="cpm-runway__hint">
            {limits?.autoTopUpEnabled
              ? t('common.show.cpmAutoReloadOn').replace(
                  '{{amount}}',
                  formatCoins(limits.autoTopUpAmountCoins),
                )
              : t('common.show.cpmAutoReloadHint')}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
